import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ContactPhoneIcon from "@material-ui/icons/ContactPhone";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import {
  addPendingMessage,
  markPendingMessageFailed,
} from "../../services/pendingMessages";
import ContactCardModal from "../ContactCardModal";
import LocationModal from "../LocationModal";

let Mp3Recorder = null;

const initRecorder = async () => {
  if (!Mp3Recorder) {
    try {
      const MicRecorder = (await import("mic-recorder-to-mp3")).default;
      Mp3Recorder = new MicRecorder({ bitRate: 128 });
    } catch (error) {
      console.error("Failed to initialize recorder:", error);
      return null;
    }
  }
  return Mp3Recorder;
};

const formatFileSize = size => {
  if (!size) {
    return "";
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background: theme.palette.background.paper,
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: theme.palette.type === "dark" ? "#111827" : "#fff",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(148, 163, 184, 0.18)"
        : "1px solid transparent",
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    color: theme.palette.text.primary,
  },

  sendMessageIcons: {
    color: theme.palette.text.secondary,
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "12px 14px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "stretch",
    backgroundColor: theme.palette.background.paper,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    gap: 12,
  },

  mediaPreviewContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  mediaPreviewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    minWidth: 0,
  },

  mediaPreviewTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },

  mediaPreviewSubtitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  mediaPreviewCount: {
    flexShrink: 0,
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 700,
  },

  mediaPreviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 8,
    width: "100%",
  },

  mediaPreviewCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    padding: "8px 10px",
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#111827" : "#fff",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(148, 163, 184, 0.15)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 2px rgba(0, 0, 0, 0.35)"
        : "0 1px 2px rgba(15, 23, 42, 0.06)",
  },

  mediaPreviewThumb: {
    width: 44,
    height: 44,
    borderRadius: 10,
    objectFit: "cover",
    flexShrink: 0,
    backgroundColor: "#e5e7eb",
    border: "1px solid rgba(15, 23, 42, 0.08)",
  },

  mediaPreviewFallback: {
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e0f2fe",
    color: "#0369a1",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    border: "1px solid rgba(3, 105, 161, 0.12)",
  },

  mediaPreviewMeta: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  mediaPreviewName: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  mediaPreviewInfo: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  mediaPreviewRemoveButton: {
    flexShrink: 0,
    padding: 4,
  },

  mediaCaptionInput: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 10,
    backgroundColor: theme.palette.type === "dark" ? "#111827" : "#fff",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(148, 163, 184, 0.18)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    color: theme.palette.text.primary,
  },

  mediaPreviewActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },

  mediaPreviewHint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },

  mediaHdOption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "8px 10px",
    borderRadius: 10,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(59, 130, 246, 0.12)"
        : "rgba(37, 99, 235, 0.07)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(96, 165, 250, 0.25)"
        : "1px solid rgba(37, 99, 235, 0.16)",
  },

  mediaHdOptionText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  mediaHdOptionTitle: {
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 600,
  },

  mediaHdOptionHelp: {
    color: theme.palette.text.secondary,
    fontSize: 11,
  },

  mobileMenuItem: {
    minHeight: 58,
    paddingLeft: 18,
    paddingRight: 18,
  },

  mobileMenuIconSlot: {
    width: 38,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    color: theme.palette.text.secondary,
  },

  mobileMenuLabel: {
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
  },

  mobileMenuFileLabel: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid rgba(148, 163, 184, 0.18)",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    color: theme.palette.text.primary,
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        color: theme.palette.text.primary,
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          background:
            theme.palette.type === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "#F1F1F1",
          cursor: "pointer",
        },
      },
    },
  },
}));

const MessageInput = ({ ticketStatus, sendTicketId }) => {
  const classes = useStyles();
  const { ticketId } = useParams();
  const targetTicketId = sendTicketId || ticketId;

  const [medias, setMedias] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [sendImagesAsHd, setSendImagesAsHd] = useState(false);
  const [mediaCaption, setMediaCaption] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  // Cola de envíos de texto: los mensajes salen uno tras otro (en orden) sin
  // bloquear el campo de escritura mientras WhatsApp confirma cada uno.
  const sendQueueRef = useRef(Promise.resolve());
  const [anchorEl, setAnchorEl] = useState(null);
  const [contactCardModalOpen, setContactCardModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setSendImagesAsHd(false);
      setMediaCaption("");
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    if (!medias.length) {
      setMediaPreviews([]);
      return undefined;
    }

    const nextPreviews = medias.map((media, index) => {
      const isImage = media.type?.startsWith("image/");
      return {
        id: `${media.name}-${media.size}-${index}`,
        name: media.name,
        type: media.type || "file",
        sizeLabel: formatFileSize(media.size),
        previewUrl: isImage ? URL.createObjectURL(media) : "",
      };
    });

    setMediaPreviews(nextPreviews);

    return () => {
      nextPreviews.forEach(preview => {
        if (preview.previewUrl) {
          URL.revokeObjectURL(preview.previewUrl);
        }
      });
    };
  }, [medias]);

  const handleChangeInput = e => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setSendImagesAsHd(false);
    setMediaCaption("");
    setMedias(selectedMedias);
  };

  const handleRemoveMedia = mediaIndex => {
    setMedias(currentMedias =>
      currentMedias.filter((_, index) => index !== mediaIndex)
    );
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setSendImagesAsHd(false);
      setMediaCaption("");
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    e.preventDefault();
    let pendingMedias = [...medias];

    try {
      for (const media of medias) {
        const formData = new FormData();
        formData.append("fromMe", true);

        if (replyingMessage?.id) {
          formData.append("quotedMsgId", replyingMessage.id);
          formData.append(
            "quotedMsgFromMe",
            String(Boolean(replyingMessage.fromMe))
          );
        }

        formData.append("medias", media);
        formData.append("body", mediaCaption.trim() || media.name);
        formData.append(
          "sendMediaAsHd",
          String(sendImagesAsHd && media.type?.startsWith("image/"))
        );

        await api.post(`/messages/${targetTicketId}`, formData);
        pendingMedias = pendingMedias.filter(
          pendingMedia => pendingMedia !== media
        );
        setMedias(pendingMedias);
      }

      setReplyingMessage(null);
      setSendImagesAsHd(false);
      setMediaCaption("");
    } catch (err) {
      toastError(err);
      setMedias(pendingMedias);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() === "") return;

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    const pendingMessageId = addPendingMessage({
      ticketId: targetTicketId,
      body: message.body,
      quotedMsg: replyingMessage,
    });
    message.clientMessageId = pendingMessageId;

    setInputMessage("");
    setShowEmoji(false);
    setReplyingMessage(null);

    const ticketIdToSend = targetTicketId;
    sendQueueRef.current = sendQueueRef.current.then(async () => {
      try {
        await api.post(`/messages/${ticketIdToSend}`, message);
      } catch (err) {
        markPendingMessageFailed(ticketIdToSend, pendingMessageId);
        toastError(err);
      }
    });
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();
      if (!recorder) {
        throw new Error("Recorder not available");
      }
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: inputMessage.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const recorder = await initRecorder();
      if (!recorder) {
        throw new Error("Recorder not available");
      }
      const [, blob] = await recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `${new Date().getTime()}.mp3`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${targetTicketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      const recorder = await initRecorder();
      if (recorder) {
        await recorder.stop().getMp3();
      }
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = () => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  const mediaCountLabel =
    medias.length === 1 ? "1 archivo listo para enviar" : `${medias.length} archivos listos para enviar`;
  const hasSelectedImages = medias.some(media =>
    media.type?.startsWith("image/")
  );

  if (medias.length > 0)
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <IconButton
          aria-label="cancel-upload"
          component="span"
          onClick={() => {
            setMedias([]);
            setSendImagesAsHd(false);
            setMediaCaption("");
          }}
        >
          <CancelIcon className={classes.sendMessageIcons} />
        </IconButton>

        <div className={classes.mediaPreviewContent}>
          <div className={classes.mediaPreviewHeader}>
            <div>
              <div className={classes.mediaPreviewTitle}>{mediaCountLabel}</div>
              <div className={classes.mediaPreviewSubtitle}>
                Revisa la selección antes de enviarla al cliente.
              </div>
            </div>
            <span className={classes.mediaPreviewCount}>{medias.length}</span>
          </div>

          {loading ? (
            <div>
              <CircularProgress className={classes.circleLoading} />
            </div>
          ) : (
            <div className={classes.mediaPreviewGrid}>
              {mediaPreviews.map((preview, index) => {
                const fileTypeLabel = preview.type.startsWith("image/")
                  ? "Imagen"
                  : preview.type.startsWith("audio/")
                    ? "Audio"
                    : preview.type.startsWith("video/")
                      ? "Video"
                      : "Archivo";

                return (
                  <div key={preview.id} className={classes.mediaPreviewCard}>
                    {preview.previewUrl ? (
                      <img
                        src={preview.previewUrl}
                        alt={preview.name}
                        className={classes.mediaPreviewThumb}
                      />
                    ) : (
                      <div className={classes.mediaPreviewFallback}>
                        {fileTypeLabel}
                      </div>
                    )}

                    <div className={classes.mediaPreviewMeta}>
                      <span className={classes.mediaPreviewName}>{preview.name}</span>
                      <span className={classes.mediaPreviewInfo}>
                        {fileTypeLabel} {preview.sizeLabel ? `· ${preview.sizeLabel}` : ""}
                      </span>
                    </div>

                    <IconButton
                      aria-label={`remove-media-${index}`}
                      component="span"
                      className={classes.mediaPreviewRemoveButton}
                      onClick={() => handleRemoveMedia(index)}
                    >
                      <HighlightOffIcon className={classes.cancelAudioIcon} />
                    </IconButton>
                  </div>
                );
              })}
            </div>
          )}

          {hasSelectedImages && (
            <div className={classes.mediaHdOption}>
              <div className={classes.mediaHdOptionText}>
                <span className={classes.mediaHdOptionTitle}>
                  {i18n.t("messagesInput.sendImagesAsHd")}
                </span>
                <span className={classes.mediaHdOptionHelp}>
                  {i18n.t("messagesInput.sendImagesAsHdHelp")}
                </span>
              </div>
              <Switch
                size="small"
                color="primary"
                checked={sendImagesAsHd}
                onChange={event => setSendImagesAsHd(event.target.checked)}
                inputProps={{
                  "aria-label": i18n.t("messagesInput.sendImagesAsHd"),
                }}
              />
            </div>
          )}

          <InputBase
            className={classes.mediaCaptionInput}
            placeholder={i18n.t("messagesInput.mediaCaptionPlaceholder")}
            multiline
            maxRows={4}
            value={mediaCaption}
            disabled={loading}
            onChange={e => setMediaCaption(e.target.value)}
            onKeyPress={e => {
              if (loading || e.shiftKey) return;
              if (e.key === "Enter") {
                e.preventDefault();
                handleUploadMedia(e);
              }
            }}
          />

          <div className={classes.mediaPreviewActions}>
            <span className={classes.mediaPreviewHint}>
              {medias.length > 1
                ? "Se enviará un mensaje por cada archivo seleccionado."
                : "Se enviará el archivo seleccionado."}
            </span>
            <IconButton
              aria-label="send-upload"
              component="span"
              onClick={handleUploadMedia}
              disabled={loading}
            >
              <SendIcon className={classes.sendMessageIcons} />
            </IconButton>
          </div>
        </div>
      </Paper>
    );
  else {
    return (
      <>
        <ContactCardModal
          open={contactCardModalOpen}
          onClose={() => setContactCardModalOpen(false)}
          ticketId={targetTicketId}
        />
        <LocationModal
          open={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          ticketId={targetTicketId}
        />
        <Paper square elevation={0} className={classes.mainWrapper}>
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || recording || ticketStatus !== "open"}
              onClick={() => setShowEmoji(prevState => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                  <Picker
                    perLine={16}
                    showPreview={false}
                    showSkinTones={false}
                    onSelect={handleAddEmoji}
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || ticketStatus !== "open"}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                aria-label="upload"
                component="span"
                disabled={loading || recording || ticketStatus !== "open"}
              >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            <Tooltip title={i18n.t("messagesInput.sendContactCard")}>
              <span>
                <IconButton
                  aria-label="send-contact-card"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                  onClick={() => setContactCardModalOpen(true)}
                >
                  <ContactPhoneIcon className={classes.sendMessageIcons} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={i18n.t("messagesInput.sendLocation")}>
              <span>
                <IconButton
                  aria-label="send-location"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                  onClick={() => setLocationModalOpen(true)}
                >
                  <LocationOnIcon className={classes.sendMessageIcons} />
                </IconButton>
              </span>
            </Tooltip>
            <FormControlLabel
              style={{ marginRight: 7, color: "gray" }}
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={e => {
                    setSignMessage(e.target.checked);
                  }}
                  name="showAllTickets"
                  color="primary"
                />
              }
            />
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleOpenMenuClick}
            >
              <MoreVert></MoreVert>
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
              PaperProps={{ style: { minWidth: 250 } }}
            >
              <MenuItem
                className={classes.mobileMenuItem}
                disabled={loading || recording || ticketStatus !== "open"}
                onClick={() => {
                  setAnchorEl(null);
                  setShowEmoji(prevState => !prevState);
                }}
              >
                <span className={classes.mobileMenuIconSlot}>
                  <MoodIcon className={classes.sendMessageIcons} />
                </span>
                <span className={classes.mobileMenuLabel}>
                  {i18n.t("messagesInput.emojis")}
                </span>
              </MenuItem>
              <MenuItem
                className={classes.mobileMenuItem}
                disabled={loading || recording || ticketStatus !== "open"}
                onClick={() => {
                  setAnchorEl(null);
                  setLocationModalOpen(true);
                }}
              >
                <span className={classes.mobileMenuIconSlot}>
                  <LocationOnIcon className={classes.sendMessageIcons} />
                </span>
                <span className={classes.mobileMenuLabel}>
                  {i18n.t("messagesInput.sendLocation")}
                </span>
              </MenuItem>
              <MenuItem
                className={classes.mobileMenuItem}
                disabled={loading || recording || ticketStatus !== "open"}
                onClick={() => {
                  setAnchorEl(null);
                  setContactCardModalOpen(true);
                }}
              >
                <span className={classes.mobileMenuIconSlot}>
                  <ContactPhoneIcon className={classes.sendMessageIcons} />
                </span>
                <span className={classes.mobileMenuLabel}>
                  {i18n.t("messagesInput.sendContactCard")}
                </span>
              </MenuItem>
              <MenuItem
                className={classes.mobileMenuItem}
                onClick={handleMenuItemClick}
              >
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={loading || recording || ticketStatus !== "open"}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label
                  htmlFor="upload-button"
                  className={classes.mobileMenuFileLabel}
                >
                  <span className={classes.mobileMenuIconSlot}>
                    <AttachFileIcon className={classes.sendMessageIcons} />
                  </span>
                  <span className={classes.mobileMenuLabel}>
                    {i18n.t("messagesInput.attachFiles")}
                  </span>
                </label>
              </MenuItem>
              <MenuItem className={classes.mobileMenuItem}>
                <span className={classes.mobileMenuIconSlot} />
                <span className={classes.mobileMenuLabel}>
                  {i18n.t("messagesInput.signMessage")}
                </span>
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={e => setSignMessage(e.target.checked)}
                  name="showAllTickets"
                  color="primary"
                />
              </MenuItem>
            </Menu>
          </Hidden>
          <div className={classes.messageInputWrapper}>
            <InputBase
              inputRef={input => {
                input && input.focus();
                input && (inputRef.current = input);
              }}
              className={classes.messageInput}
              placeholder={
                ticketStatus === "open"
                  ? i18n.t("messagesInput.placeholderOpen")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || ticketStatus !== "open"}
              onPaste={e => {
                ticketStatus === "open" && handleInputPaste(e);
              }}
              onKeyPress={e => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.messageQuickAnswersWrapperItem}
                      key={index}
                    >
                      <a onClick={() => handleQuickAnswersClick(value.message)}>
                        {`${value.shortcut} - ${value.message}`}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div></div>
            )}
          </div>
          {inputMessage ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={loading}
            >
              <SendIcon className={classes.sendMessageIcons} />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <IconButton
              aria-label="showRecorder"
              component="span"
              disabled={loading || ticketStatus !== "open"}
              onClick={handleStartRecording}
            >
              <MicIcon className={classes.sendMessageIcons} />
            </IconButton>
          )}
        </div>
        </Paper>
      </>
    );
  }
};

export default MessageInput;
